import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { es } from 'date-fns/locale';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import Button from '@/components/Button';
import OptionButton from '@/components/Button/otherButtons/OptionButton';
import InputField from '@/components/InputField';
import RangeDatePicker from '@/components/RangeDatePicker/RangeDatePicker';
import { datePickerSlotProps } from '@/utils/datePickerConfig';

import type { IReqCreateReport } from '@/types/parcel/orders/reports.interface';

import { initialStateReports, STATUS_V3 } from './constants';
import { schema } from './schemaReports';

interface ReportsModalProps {
  handleNewReport: (ars: IReqCreateReport) => void;
  handleReportFields: (field: keyof IReqCreateReport, value: string | string[]) => void;
  reportFields: IReqCreateReport;
  openCloseReportsWithRefresh: () => void;
}

const ReportsModal = ({ handleNewReport, openCloseReportsWithRefresh }: ReportsModalProps) => {
  const theme = useTheme();
  const [optionSelected, setOptionSelected] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialStateReports,
    mode: 'onBlur',
  });

  const { start, finish } = watch('betweenDates');
  const note = watch('note');
  const status = watch('status');
  const isFormComplete = !!start && !!finish && !!(note?.trim() ?? '') && (status?.length ?? 0) > 0;

  const handleOptionSelected = (option: number) => {
    setOptionSelected(option);
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDate = (dateStr: string) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const changeStatus = (_: any, value: Array<{ value: string; label: string }>) => {
    if (value.length === 0) {
      setValue('status', []);
      return;
    }

    const isAllStatusSelected = value[value?.length - 1].value === '*';
    const isAllStatus = value.some((status) => status.value === '*');

    let parsedStatus = value;
    if (isAllStatusSelected) {
      parsedStatus = [
        {
          value: '*',
          label: 'Todos los estatus',
        },
      ];
    } else if (isAllStatus && !isAllStatusSelected) {
      parsedStatus = value.filter((status) => status.value !== '*');
    }
    setValue('status', parsedStatus);
  };

  const onSubmit = (data: any) => {
    const status = data.status.some(
      (status: { value: string; label: string }) => status.value === '*',
    )
      ? []
      : data.status.map((s: { value: string; label: string }) => s.value);

    handleNewReport({
      ...data,
      status,
    });
    openCloseReportsWithRefresh();
  };

  return (
    <Box
      sx={{
        minHeight: optionSelected !== 0 ? { xxs: '450px', sm: '420px' } : 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: optionSelected !== 0 ? 'space-between' : 'flex-start',
      }}
    >
      {optionSelected === 0 ? (
        <>
          <Typography variant="subtitle2">Elige el período para tu reporte</Typography>

          <Box sx={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <OptionButton handleClick={() => handleOptionSelected(1)}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: theme.palette.secondary.main,
                  fontWeight: 600,
                  '& span': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                Por <span>día</span>
              </Typography>
            </OptionButton>
            <OptionButton handleClick={() => handleOptionSelected(2)}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: theme.palette.secondary.main,
                  fontWeight: 600,
                  '& span': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                Por <span>rango de </span>
                <span>fechas</span>
              </Typography>
            </OptionButton>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            flex: 1,
            gap: '1.5rem',
          }}
        >
          {optionSelected === 1 && (
            <Box>
              <Typography variant="subtitle2" mb={2}>
                Seleccionar fecha
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <Controller
                  name="betweenDates.start"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Fecha"
                      sx={{ width: '100%' }}
                      maxDate={new Date()}
                      onChange={(date) => {
                        const formatted = formatDate(date);
                        setValue('betweenDates.start', formatted || '');
                        setValue('betweenDates.finish', formatted || '');
                        field.onChange(formatted || '');
                      }}
                      value={field.value ? parseDate(field.value) : null}
                      slots={{ textField: (params) => <TextField {...params} /> }}
                      slotProps={datePickerSlotProps}
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>
          )}
          {optionSelected === 2 && (
            <Box>
              <Typography variant="subtitle2" mb={2}>
                Seleccionar rango de fechas
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                <RangeDatePicker
                  startLabel="Desde"
                  endLabel="Hasta"
                  untilToday
                  noPadding
                  onChange={(e, v) => {
                    if (e == 'startDate') {
                      setValue('betweenDates.start', v);
                    }
                    if (e == 'endDate') {
                      setValue('betweenDates.finish', v);
                    }
                  }}
                />
              </LocalizationProvider>
              {errors?.betweenDates?.finish && (
                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                  {errors.betweenDates.finish.message}
                </Typography>
              )}
            </Box>
          )}
          <Box>
            <Typography variant="subtitle2" mb={0.5}>
              ¿Deseas agregar una descripción a tu reporte?
            </Typography>
            <InputField
              label=""
              name="note"
              register={register}
              errors={errors}
              placeholder="Mensual Enero"
              textFieldProps={{
                variant: 'outlined',
                fullWidth: true,
                multiline: false,
                margin: 'none',
                sx: { mt: 0 },
              }}
            />
          </Box>

          <Grid size={{ xxs: 12, md: 6 }}>
            <Autocomplete
              multiple
              options={STATUS_V3}
              getOptionLabel={(option) => option.label}
              value={watch('status')}
              onChange={changeStatus}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Elige los campos a incluir en tu reporte"
                  placeholder="Estatus"
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid
            size={{ xxs: 12 }}
            container
            sx={{
              display: 'flex',
              justifyContent: { xxs: 'center', xs: 'space-between' },
              gap: '0.5rem',
            }}
          >
            <Button variant="outlined" onClick={() => handleOptionSelected(0)}>
              Regresar
            </Button>
            <Button disabled={!isFormComplete} onClick={handleSubmit(onSubmit)}>
              Crear reporte
            </Button>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default ReportsModal;
